import { RecordManagementLabel } from '@aibees/elephant/feature/search-drawer/components/record-management-labels/components/record-management-label';
import {
  useCompanyFieldDisabledState,
  useCompanyTooltipTitle,
} from '@aibees/elephant/hooks';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { LabelManagementProvider } from '../../../search-table-action-dialog-lists/context';
import { DEFAULT_MAX_LABELS_TO_SHOW } from '../../constants';
import { useLabelAction } from '../../hooks';
import { LabelActionDialog } from '../label-action-dialog';
import * as SX from './sx';
import type { LabelActionRecordManagementProps as Props } from './types';

export const LabelActionRecordManagement = memo(
  ({
    className,
    type,
    currentLabelIds,
    id,
    searchTypeOverride,
    onLabelsSelect,
    selectedLabels,
    maxLabelsToShow = DEFAULT_MAX_LABELS_TO_SHOW,
  }: Props) => {
    const labelAction = useLabelAction({
      type,
      currentLabelIds,
      id,
      searchTypeOverride,
      onLabelsSelect,
      selectedLabels,
    });

    const { currentLabels, isLabel, emptyTitle, handleOpenDialog, searchType } =
      labelAction;

    const { shouldDisable } = useCompanyFieldDisabledState(searchType);
    const tooltipTitle = useCompanyTooltipTitle(shouldDisable);

    const addEditButton = useMemo(
      () => (
        <Button
          color="inherit"
          variant="outlined"
          size="xsmall"
          startIcon={
            <Icon name={!(currentLabels.length > 0) ? 'plus' : 'edit'} />
          }
          onClick={handleOpenDialog}
          disabled={shouldDisable}
        >
          {!(currentLabels.length > 0) ? 'Add' : 'Edit'}
        </Button>
      ),
      [currentLabels.length, handleOpenDialog, shouldDisable]
    );

    return (
      <LabelManagementProvider onChangeLabels={() => {}}>
        <Stack
          className={classnames('LabelActionRecordManagement', className)}
          sx={SX.root}
          onClick={(e) => e.stopPropagation()}
        >
          <Stack
            sx={SX.header}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Icon
                name={isLabel ? 'label' : 'list-outline'}
                color="grey.500"
                size={18}
              />
              <Typography variant="body3" color="grey.700">
                {emptyTitle}:
              </Typography>
            </Stack>

            {shouldDisable && tooltipTitle ? (
              <Tooltip placement="right" title={tooltipTitle}>
                <span>{addEditButton}</span>
              </Tooltip>
            ) : (
              addEditButton
            )}
          </Stack>

          {currentLabels.length > 0 && (
            <Stack sx={SX.labelWrapper}>
              {currentLabels.slice(0, maxLabelsToShow).map((label) => (
                <RecordManagementLabel key={label.id} label={label} id={id} />
              ))}
            </Stack>
          )}

          {currentLabels.length > maxLabelsToShow && (
            <Button
              variant="text"
              size="xsmall"
              color="primary"
              onClick={handleOpenDialog}
            >
              +{currentLabels.length - maxLabelsToShow} View All
            </Button>
          )}
        </Stack>
        <LabelActionDialog labelAction={labelAction} type={type} />
      </LabelManagementProvider>
    );
  }
);

LabelActionRecordManagement.displayName = 'LabelActionRecordManagement';
